import { useState } from "react";
import { thumbFor } from "../lib/videoThumb";

/* The video container beside every exercise: a still, the file's first frame, or — when there is
 * no video at all — ONE neutral dumbbell, medium, in proportion to the tile.
 *
 * 🔴 THE BRAND MARK USED TO SIT HERE AND IT HAD TO GO (Fable's call, 2026-09-18, reasons weighed).
 * The empty tile drew the ensō C at 45% of the box in `--ht-accent`, and the owner read his own
 * library as broken: *"few still look broken"* — a centred red arc on a dark tile is a loading
 * spinner to every eye that has ever used a computer. One grave reason beat the brand-moment
 * argument: a mark that reads as a spinner damages the brand more than its absence does, and the
 * plate keeps its real homes (the icon, the nav, the launch). ⚠️ Dimming was considered and
 * REJECTED — an arc of any size is still an arc, so the shape had to change, not its weight.
 *
 * ⚠️ AND THE EMPTY STATE MUST NOT BE CONFUSABLE WITH A TILE WHOSE IMAGE HAS NOT ARRIVED YET, which
 * is the whole reason the play dot now waits for `onLoad`. Two states, two readings, no overlap:
 *   glyph drawn   → there is definitely no video
 *   plain surface → a picture is on its way
 * Before this, an unloaded tile showed the play dot over an empty box, which is exactly what the
 * owner was looking at when he said the page still looked broken.
 */
export function VideoThumb({ demoVideoUrl, thumbnailUrl, size = "row" }: {
  /** `mini` is the Library row's 48 px square, `square` fills a shelf grid cell (W72 step 5). */
  demoVideoUrl: string | null; thumbnailUrl: string | null; size?: "row" | "card" | "mini" | "square";
}) {
  const t = thumbFor(demoVideoUrl, thumbnailUrl);
  const [arrived, setArrived] = useState(false);
  return (
    <span className={`ht-thumb ht-thumb-${size}${t.kind === "none" ? " is-empty" : ""}`} aria-hidden="true">
      {t.kind === "image" && <img src={t.src} alt="" loading="lazy" onLoad={() => setArrived(true)} />}
      {t.kind === "video" && <video src={`${t.src}#t=0.5`} muted playsInline preload="metadata" />}
      {t.kind === "link" && (
        /* 🔴 R17 — THERE IS A VIDEO AND THIS PAGE MAY NOT PLAY IT. The dumbbell would be a
           lie (it means "no video") and a `<video src>` would be a dead player, which is the
           defect. An arrow leaving a frame says the true thing: it exists, it is elsewhere.
           ⚠️ AND NO PLAY DOT. The dot promises playback in place, and this tile cannot deliver
           it — the promise is what turns a limitation into a broken feature. */
        <svg className="ht-thumb-offsite" viewBox="0 0 40 30" fill="none" stroke="currentColor"
             strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
          <g vectorEffect="non-scaling-stroke">
            <path d="M22 9 H10.5 a1.5 1.5 0 0 0 -1.5 1.5 V20 a1.5 1.5 0 0 0 1.5 1.5 H24 a1.5 1.5 0 0 0 1.5 -1.5 V16"
                  vectorEffect="non-scaling-stroke" />
            <path d="M22.5 12.5 L31 7" vectorEffect="non-scaling-stroke" />
            <path d="M26.5 6.5 H31.5 V11" vectorEffect="non-scaling-stroke" />
          </g>
        </svg>
      )}
      {t.kind === "none" && (
        /* A DUMBBELL, medium, tilted — his word on the struck play triangle that stood here for
           an hour (2026-09-18 ~22:25): *"instead of this ugly small play button with the line,
           use a medium nice size for the containers, dumbbell icon or something like that, make
           it look cooler."* Sized as a fraction of the tile (CSS), so the row, the builder head
           and the grid card each get a glyph in proportion; the stroke is NON-SCALING so the line
           weight stays the hairline at every size. Plates carry a faint fill for weight. No
           accent (an accent shape on an empty tile reads as a state, not an object), no frame,
           no dashed border. The two-state invariant above is unchanged: glyph = no video. */
        <svg className="ht-thumb-novideo" viewBox="0 0 40 30" fill="none" stroke="currentColor"
             strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
          <g transform="rotate(-28 20 15)" vectorEffect="non-scaling-stroke">
            <path d="M13.5 15 H26.5" vectorEffect="non-scaling-stroke" />
            <path d="M3.5 15 H6 M34 15 H36.5" vectorEffect="non-scaling-stroke" />
            <rect x="9.5" y="8.5" width="4" height="13" rx="1.2" fill="currentColor" fillOpacity={0.14} vectorEffect="non-scaling-stroke" />
            <rect x="26.5" y="8.5" width="4" height="13" rx="1.2" fill="currentColor" fillOpacity={0.14} vectorEffect="non-scaling-stroke" />
            <rect x="6" y="10.75" width="3.5" height="8.5" rx="1" fill="currentColor" fillOpacity={0.14} vectorEffect="non-scaling-stroke" />
            <rect x="30.5" y="10.75" width="3.5" height="8.5" rx="1" fill="currentColor" fillOpacity={0.14} vectorEffect="non-scaling-stroke" />
          </g>
        </svg>
      )}
      {(t.kind === "video" || (t.kind === "image" && arrived)) && <span className="ht-thumb-play" />}
    </span>
  );
}
